import { useSWR } from '@tiger/swr';
import { Button, Tooltip } from 'antd';
import classNames from 'classnames';
import type { FC } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import BottomBox from 'app/components/BottomBox';
import ErrorBox from 'components/ErrorBox';
import InfoTag from 'components/InfoTag';
import Loading from 'components/Loading';
import { useConfirmModal } from 'components/Modal';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import { ReactComponent as IconEdit } from 'static/images/icons/edit.svg';
import stopIcon from 'static/images/icons/stop.png';
import APP from 'stores/APP';
import security from 'stores/security';
import API from 'utils/API';
import { formatDate, formatMoney } from 'utils/format';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import { getMaskText } from 'utils/util';
import { periodOptions } from '../components/DepositPeriodSelect';
import { RecurringScheduleStatus } from '../constants';
import { PeriodPlanHistory, type PeriodPlanListItem } from '../types';
import styles from './style.scss';

interface ScheduleDetailsProps {
    isIRA: boolean;
}

const eventAction = 'ACH 入金计划';

const ScheduleDetails: FC<ScheduleDetailsProps> = ({ isIRA }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams<{ id: string }>();
    const { isLoading, error, data, mutate } = useSWR<{
        detail: PeriodPlanListItem;
        record: PeriodPlanHistory;
    }>(!!params.id && `deposit.ach.recurring.schedule_${params.id}`, {
        fetcher: async () => {
            const reqParams = {
                account_id: security.currentAccountId,
                customer_id: security.currentCustomerId
            };
            const [detailResp, recordResp] = await Promise.all([
                http.get<PeriodPlanListItem>(API.TRADE.funding.getDepositAchPlanDetail(params.id!), {
                    params: reqParams
                }),
                http.get<PeriodPlanHistory>(API.TRADE.funding.getDepositAchPlanHistory(params.id!), {
                    params: reqParams
                })
            ]);

            return {
                detail: detailResp.data,
                record: recordResp.data
            };
        }
    });

    const { detail, record } = data ?? {};
    const periodTypeText = periodOptions.find(({ value }) => value === detail?.periodType)?.label;

    const { open: openConfirmModal } = useConfirmModal();

    const handleEdit = () => {
        if (!detail) {
            return;
        }

        APP.trackEvent('入金', eventAction, '编辑定时入金计划_点击');

        navigate(`/profile/deposit/ach/recurring/schedules/${detail.id}/edit`, {
            state: {
                next: location.pathname
            }
        });
    };

    const handleStop = () => {
        APP.trackEvent('入金', eventAction, '终止计划');

        openConfirmModal({
            content: isIRA
                ? __('Are you sure to stop the recurring contribution schedule?')
                : __('Are you sure to stop the recurring deposit schedule?'),
            okText: __('确认'),
            cancelText: __('取消'),
            onOk: async () => {
                APP.trackEvent('入金', eventAction, '确认终止计划');

                try {
                    await http.post(
                        API.TRADE.funding.modifyDepositAchPlan(params.id!),
                        {
                            status: 'TERMINATED'
                        },
                        {
                            useForm: true,
                            params: {
                                account_id: security.currentAccountId,
                                customer_id: security.currentCustomerId
                            }
                        }
                    );

                    await Toast.show(__('终止成功'));
                    mutate();
                } catch (err) {
                    Toast.show(err);
                }
            },
            onCancel: () => {
                APP.trackEvent('入金', eventAction, '取消终止计划');
            }
        });
    };

    if (isLoading) {
        return <Loading tip={__('请稍等...')} />;
    }

    if (error) {
        return <ErrorBox error={error} onClick={() => mutate()} />;
    }

    return (
        <>
            <ScrollTop />
            <div className={styles.pageRoot}>
                {detail && (
                    <div className={styles.details}>
                        <div className={styles.header}>
                            <div className={styles.title}>{__('计划详情')}</div>
                            {detail.status === 'RUNNING' ? (
                                <div className={styles.btns}>
                                    <Tooltip title={__('计划详情')} placement="bottom">
                                        <IconEdit className={styles.editIcon} fontSize={20} onClick={handleEdit} />
                                    </Tooltip>
                                    <Tooltip title={__('终止计划')} placement="bottom">
                                        <img
                                            className={classNames(styles.stopIcon, 'ml-3')}
                                            src={stopIcon}
                                            alt={__('终止计划')}
                                            onClick={handleStop}
                                        />
                                    </Tooltip>
                                </div>
                            ) : null}
                        </div>
                        <InfoTag
                            label={isIRA ? __('Amount of Each Contribution') : __('Amount of Each Deposit')}
                            content={`${formatMoney(detail.amount)}`}
                        />
                        <InfoTag label={__('频度')} content={periodTypeText} />
                        <InfoTag label={__('银行账号')} content={`${getMaskText(detail.bankAccount)}`} />
                        <InfoTag
                            label={isIRA ? __('Next Contribution Time') : __('Next Deposit Time')}
                            content={`${formatDate(detail.nextExecuteTime)}`}
                            contentColor="#fe9045"
                        />
                        <InfoTag label={__('当前状态')} content={RecurringScheduleStatus[detail.status]?.text} />
                        <InfoTag label={__('创建时间')} content={formatDate(detail.createdAt, 'YYYY-MM-DD HH:mm:ss')} />
                    </div>
                )}
                {record?.total ? (
                    <div>
                        <div className={styles.title}>{isIRA ? __('Contribution Records') : __('Deposit Records')}</div>
                        <p>
                            {printf(
                                isIRA
                                    ? __('A total of contribution records: %s times; Succeeded: %s times.')
                                    : __('A total of deposit records: %s times; Succeeded: %s times.'),
                                record.total,
                                record.succCount
                            )}
                        </p>
                        {(record?.depositApplications ?? []).map(item => (
                            <Link
                                key={item.id}
                                state={{ record: item }}
                                to={{
                                    pathname: `/profile/deposit/ach/recurring/schedules/${params.id!}/record`
                                }}>
                                <div className={styles.record}>
                                    <img
                                        className={styles.avatar}
                                        src={RecurringScheduleStatus[item.status]?.icon}
                                        alt="icon"
                                    />
                                    <div className={styles.detail}>
                                        <div className={styles.title}>
                                            <div className={styles.amount}>
                                                <span>{formatMoney(item.amount)}</span>
                                                {item.currency}
                                            </div>
                                            {item.account ? (
                                                <div className={styles.account}>{getMaskText(item.account)}</div>
                                            ) : null}
                                        </div>
                                        <div className={styles.description}>
                                            <span>
                                                {item.createdAt ? formatDate(item.createdAt, 'YYYY-MM-DD HH:mm') : ''}
                                            </span>
                                            <span
                                                style={{
                                                    color: `${RecurringScheduleStatus[item.status]?.color || 'inherit'}`
                                                }}>
                                                {RecurringScheduleStatus[item.status]?.text || item.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : null}
                <BottomBox className="mt-5">
                    <Button className="btn-important" type="primary" block onClick={() => navigate(-1)}>
                        {__('Back')}
                    </Button>
                </BottomBox>
            </div>
        </>
    );
};

export default ScheduleDetails;
